import { useEffect, useRef, useState } from "react";
import { submitForm } from "@/lib/api";

export default function ContactForm() {
  const successRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    setError("");
    try {
      await submitForm("contact", form);
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Unable to send your message right now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="py-24 px-6 bg-white">
      <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
        {/* Left info panel */}
        <div className="lg:col-span-2">
          <span className="inline-flex items-center gap-2 text-[#1A4D3C] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
            <span className="w-8 h-px bg-[#C9A961]" />
            Get in Touch
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
            Send us a message.
          </h2>
          <p className="text-[#6B6B6B] text-base leading-relaxed mb-10">
            Complete the form and a member of our team will review your inquiry
            and follow up. All conversations are handled with professionalism
            and discretion.
          </p>

          <div className="form-intro-details space-y-6 mb-10">
            {[
              {
                icon: "ri-map-pin-2-line",
                title: "Nairobi & Fort Lauderdale",
                desc: "Nairobi is our intended East African anchor, with connections through Fort Lauderdale, Florida.",
              },
              {
                icon: "ri-global-line",
                title: "Global Engagement",
                desc: "We welcome inquiries from people on the continent and across the global African diaspora.",
              },
              {
                icon: "ri-mail-line",
                title: "Professional Response",
                desc: "Every inquiry is reviewed by our team. We are committed to thoughtful, timely, and personalized responses.",
              },
              {
                icon: "ri-lock-2-line",
                title: "Share Only What Is Needed",
                desc: "Please leave out confidential financial information. Our team uses your message to review and respond to your inquiry.",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#1A4D3C] text-base`} />
                </div>
                <div>
                  <div className="font-semibold text-[#2B2D2F] text-sm mb-0.5">
                    {item.title}
                  </div>
                  <div className="text-[#6B6B6B] text-xs leading-relaxed">
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <img
            loading="lazy"
            src="/assets/images/site/contact-form-img-001-560x320.jpg"
            alt="Harambee Global Ventures professional team Nairobi"
            className="form-intro-photo w-full h-[220px] object-cover object-top rounded-lg"
          />
        </div>

        {/* Right form */}
        <div className="lg:col-span-3 bg-[#F8F6F3] rounded-lg p-8 md:p-10 border border-[#E8E4DC]">
          {submitted ? (
            <div
              className="text-center py-14"
              role="status"
              tabIndex={-1}
              ref={successRef}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-[#1A4D3C] text-3xl" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#2B2D2F] mb-3">
                Message Received
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-sm mx-auto">
                Thank you for reaching out to Harambee Global Ventures. Our team
                will review your message and follow up. We look forward to
                connecting with you.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-1">
                Contact Harambee Global Ventures
              </h3>
              <p className="text-[#6B6B6B] text-sm mb-7">
                Fields marked with * are required.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="ContactForm-components-full_name"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      id="ContactForm-components-full_name"
                      type="text"
                      name="full_name"
                      autoComplete="name"
                      required
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="ContactForm-components-email"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      id="ContactForm-components-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="ContactForm-components-phone_number"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      id="ContactForm-components-phone_number"
                      type="tel"
                      name="phone_number"
                      autoComplete="tel"
                      placeholder="Include your country code"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="ContactForm-components-organization"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      Organization / Company
                    </label>
                    <input
                      id="ContactForm-components-organization"
                      type="text"
                      name="organization"
                      placeholder="Your organization (if applicable)"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="ContactForm-components-inquiry_type"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Inquiry Type *
                  </label>
                  <select
                    id="ContactForm-components-inquiry_type"
                    name="inquiry_type"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select your inquiry type</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Trading Academy">
                      Trading Academy Questions
                    </option>
                    <option value="Advisory Consultation">
                      Advisory Consultation
                    </option>
                    <option value="Partnership Opportunity">
                      Partnership Opportunity
                    </option>
                    <option value="Media or Speaking Request">
                      Media or Speaking Request
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="ContactForm-components-message"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Message *
                    <span className="ml-2 text-[#6B6B6B] font-normal normal-case tracking-normal">
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    id="ContactForm-components-message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    placeholder="Please share what you are looking for and how we can best assist you..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors resize-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="ContactForm-components-preferred_contact_method"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Preferred Contact Method
                  </label>
                  <select
                    id="ContactForm-components-preferred_contact_method"
                    name="preferred_contact_method"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select preferred method</option>
                    <option value="Email">Email</option>
                    <option value="Phone Call">Phone Call</option>
                    <option value="Video Call">
                      Video Call (Zoom / Teams)
                    </option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="No Preference">No Preference</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-md bg-[#1A4D3C] text-white font-semibold text-sm hover:bg-[#0D2E1E] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>

                {error && (
                  <p
                    className="text-sm text-red-700 bg-red-50 rounded-md p-3 text-center"
                    role="alert"
                  >
                    {error}
                  </p>
                )}

                <p className="text-[#6B6B6B] text-xs text-center leading-relaxed">
                  By submitting this form, you agree to be contacted by the
                  Harambee Global Ventures team. Your details will be sent to
                  our team to respond to your inquiry. Please do not include
                  sensitive financial information.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
